import { format, differenceInCalendarDays } from "date-fns";
import moment from "moment-timezone";
import { NewHireJourneyId } from "models/newHire";
import { convertToLocalDateString, isInPast, parseDateOnly } from "helpers/time";
import { titleize } from "helpers/string";
import { getStageInfo } from "features/Stage/helpers";
import { Task, TaskSummary, TaskStatus } from "../models";
import {
  ACTIVE_TASK_STATUSES,
  CLOSED_TASK_STATUSES,
  OPEN_TASK_STATUSES,
  TASK_STATUSES_INCLUDED_IN_COMPLETION_RATE,
} from "../constants";

export function filterMyJourneyTasks(tasks: Task[], userId?: string | number, journeyId?: string | number): Task[] {
  return tasks.filter((t) => t.assigneeId === userId && t.newHireJourneyId === journeyId);
}

export function taskPrimaryTriggerDate(action: Task) {
  return action?.startedAt ?? action?.startAt;
}

/**
 * Resolve the timezone used to render a task's date fields. Defaults to the
 * assignee's stored timezone; falls back to the viewer's browser zone only
 * when the assignee has none (e.g. unassigned task, or assignee without a
 * configured timezone).
 */
export function taskDisplayTimezone(task: Pick<TaskSummary, "assignee">): string {
  return task.assignee?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format the task's "Starts" date.
 *
 * start_at / started_at are real UTC instants — slicing the ISO string (or
 * calling toDate() on it without a zone) yields the UTC calendar date, which
 * is one day ahead for tasks that started after ~5pm Pacific. Convert in the
 * given timezone (or fall back to the assignee's) before formatting so an
 * Active task never displays a future start date.
 */
export function formatTaskStartDate(task: Task | TaskSummary, timezone?: string): string | null {
  if (task.startAfterDependencyId) return null;
  const instant = taskPrimaryTriggerDate(task);
  if (!instant) return null;
  const tz = timezone ?? taskDisplayTimezone(task);
  return format(parseDateOnly(convertToLocalDateString(String(instant), tz)), "MMM d, yyyy");
}

/**
 * Compute the day-resolution diff (whole calendar days) between the task's
 * start instant and "now", both anchored in the task's display timezone.
 * Returns null for tasks without a startAt or with a dependency.
 *
 * Used by relative-time labels like "Starts today" / "Starts tomorrow". Using
 * raw `Date.getTime()` math against the browser's notion of "now" rounds at
 * the UTC day boundary, which mis-buckets tasks scheduled late evening in
 * westward timezones (e.g. PT). Anchoring both ends in the assignee's zone
 * gives the user-perceived diff.
 */
export function taskDaysUntilStart(task: Task | TaskSummary, timezone?: string): number | null {
  if (task.startAfterDependencyId || !task.startAt) return null;
  const tz = timezone ?? taskDisplayTimezone(task);
  const startDay = moment.tz(String(task.startAt), tz).startOf("day");
  const todayInTz = moment.tz(tz).startOf("day");
  return startDay.diff(todayInTz, "days");
}

export function isTaskInPast(task: Task) {
  return !!task?.startedAt || (!task?.startedAt && isInPast(task?.startAt));
}

export function isTaskInFuture(task: Task) {
  return !task?.startedAt && (!task.startAt || !isInPast(task?.startAt));
}

export function isTaskActive(task: Task) {
  return ACTIVE_TASK_STATUSES.includes(task.status);
}

export function includeTaskInCompletionRate(task: Task) {
  return TASK_STATUSES_INCLUDED_IN_COMPLETION_RATE.includes(task.status);
}

export function isTaskActiveFailedOrUrgent(task: Task) {
  if (!task) return false;
  return ACTIVE_TASK_STATUSES.includes(task.status) || task.status === "failed" || task.isUrgent;
}

export function isTaskStartedFailed(task: Task) {
  // for tasks that are past start date and in failed state
  return isTaskInPast(task) && task?.status === "failed";
}

export interface TaskFilterOptions {
  searchText?: string;
  statusFilter?: string;
  assigneeId?: string;
  urgentOnly?: boolean;
  overdueOnly?: boolean;
  dueSoonOnly?: boolean;
  hasIssues?: boolean;
  stageKey?: string;
}

export function filterTasks(tasks: Task[], options: TaskFilterOptions = {}): Task[];
export function filterTasks(
  tasks: Task[],
  searchText?: string,
  statusFilter?: string,
  filterByAssigneeId?: string,
): Task[];
export function filterTasks(
  tasks: Task[],
  searchTextOrOptions?: string | TaskFilterOptions,
  statusFilter?: string,
  filterByAssigneeId?: string,
): Task[] {
  // Handle both old and new signatures
  const options: TaskFilterOptions =
    typeof searchTextOrOptions === "object"
      ? searchTextOrOptions
      : {
          searchText: searchTextOrOptions,
          statusFilter,
          assigneeId: filterByAssigneeId,
        };

  return tasks.filter((task) => {
    // Search filter
    if (options.searchText && options.searchText.length > 2) {
      const lower = options.searchText.toLowerCase();
      const match = task.name.toLowerCase().includes(lower) || task.assignee?.fullName?.toLowerCase().includes(lower);
      if (!match) return false;
    }

    // Status filter
    if (options.statusFilter) {
      const statusMap: Record<string, TaskStatus[]> = {
        open: ["active", "in_progress", "blocked", "failed"],
        open_with_upcoming: ["upcoming", "active", "in_progress", "blocked", "failed"],
        all: ["upcoming", "active", "in_progress", "blocked", "completed", "skipped", "removed", "closed_stale", "failed"],
        upcoming: ["upcoming", "failed"],
        closed: ["completed", "skipped", "removed", "closed_stale"],
      };

      let isValidTime = true;
      if (options.statusFilter === "open") {
        isValidTime = isTaskInPast(task);
      } else if (options.statusFilter === "upcoming") {
        isValidTime = isTaskInFuture(task);
      }

      const allowedStatuses = statusMap[options.statusFilter];
      if (!allowedStatuses?.includes(task.status) || !isValidTime) return false;
    }

    // Assignee filter
    if (options.assigneeId && task.assigneeId !== options.assigneeId) {
      return false;
    }

    // Urgent/overdue filter
    if (options.urgentOnly && !task.isUrgent && !task.isOverdue) {
      return false;
    }

    // Overdue only
    if (options.overdueOnly && !task.isOverdue) {
      return false;
    }

    // Due soon (urgent but not yet overdue)
    if (options.dueSoonOnly && (!task.isUrgent || task.isOverdue)) {
      return false;
    }

    // Has issues filter
    if (options.hasIssues && (!task.issueIds || task.issueIds.length === 0)) {
      return false;
    }

    // Stage filter
    if (options.stageKey && task.stageKey !== options.stageKey) {
      return false;
    }

    return true;
  });
}

export function pullUniqueNewHireJourneyIdsFromTasks(tasks: Task[]) {
  const uniqueNewHireJourneyIds: { [key: string]: NewHireJourneyId } = {};
  tasks.forEach((task) => {
    if (task?.newHireJourneyId) {
      uniqueNewHireJourneyIds[task.newHireJourneyId] = task.newHireJourneyId;
    }
  });
  return Object.values(uniqueNewHireJourneyIds);
}

export function showSkipButton(task: Task) {
  return !["completed", "removed", "closed_stale"].includes(task?.status);
}

/** Check if a task is in a closed state (completed, skipped, removed, or closed_stale) */
export function isTaskClosed(task: Task): boolean {
  return CLOSED_TASK_STATUSES.includes(task.status);
}

/** Check if a task is in an open state (actionable - not closed and not upcoming) */
export function isTaskOpen(task: Task): boolean {
  return OPEN_TASK_STATUSES.includes(task.status);
}

export function showMarkCompleteButton(task: Task) {
  return !["skipped", "removed", "closed_stale"].includes(task?.status);
}

export function taskActionLabel(taskAction: Task["taskAction"]) {
  switch (taskAction) {
    case "assign_buddy":
      return "Assign Buddy";
    case "create_onboarding_plan":
      return "Add Onboarding Plan";
    case "create_email_account":
      return "Add Work Email";
    case "send_message":
      return "Send Message";
    case "complete_form":
      return "Complete Form";
    case undefined:
      return "";
    default:
      return "";
  }
}

export function taskStageInfo(startDate: Date | undefined, task: Task) {
  if (!startDate || !task) {
    return { key: "", label: "" };
  }
  return getStageInfo(task.stageKey);
}

export function toTaskStatusText(status: Task["status"]) {
  if (!status) return "";
  if (status === "upcoming") return "Not Started";
  if (status === "active") return "Active";
  if (status === "closed_stale") return "Closed (stale)";
  return titleize(status);
}

/**
 * Formats a due date for display in task list rows.
 * - Overdue (past): returns null (caller shows "Overdue" separately)
 * - Due today: "Due today"
 * - Due tomorrow: "Due tomorrow"
 * - Due in 2–10 days: "Due in X days"
 * - Due in 11+ days: "Due MMM d"
 */
export function formatRelativeDueDate(dueAt: Date | string | null | undefined): string | null {
  if (!dueAt) return null;
  const days = differenceInCalendarDays(new Date(String(dueAt)), new Date());
  if (days < 0) return null;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  if (days <= 10) return `Due in ${days} days`;
  return `Due ${format(new Date(String(dueAt)), "MMM d")}`;
}
